import { FC } from "react";
import clsx from "clsx";

import { OrderSource } from "~/entities/orderEntities";
import type { SiteListObject } from "~/entities/siteEntities";
import { leadTimeFor, minimumOrderFor, settingsFor } from "~/helpers/leadTime";
import { getStoreTime, isStoreOpen } from "~/helpers/siteHelpers";
import { findOrderTypeForFulfilment } from "~/helpers/storeFinderFulfilment";
import OrderTypeDeliveryIcon from "~/icons/OrderTypeDeliveryIcon";
import OrderTypePickupIcon from "~/icons/OrderTypePickupIcon";
import TimeIcon from "~/icons/TimeIcon";

import styles from "./CateringStoreCard.module.css";

// A catering store's order types and settings, for comparing stores before choosing one
export const cateringCapabilities = (site: SiteListObject) => {
  const pickup = findOrderTypeForFulfilment(
    site.orderTypes ?? [],
    "PICKUP",
    OrderSource.CATERING,
  );
  const delivery = findOrderTypeForFulfilment(
    site.orderTypes ?? [],
    "DELIVERY",
    OrderSource.CATERING,
  );
  const pickupSettings = pickup
    ? settingsFor(site.serviceSettings, pickup.code)
    : undefined;
  const deliverySettings = delivery
    ? settingsFor(site.serviceSettings, delivery.code)
    : undefined;
  // The store list carries every org order type, so a fulfilment counts only with settings at this store
  return {
    pickup: pickupSettings ? pickup : undefined,
    delivery: deliverySettings ? delivery : undefined,
    lead: leadTimeFor(pickupSettings ?? deliverySettings),
    minPickup: minimumOrderFor(pickupSettings),
    minDelivery: minimumOrderFor(deliverySettings),
  };
};

type Props = {
  site: SiteListObject;
  // The tab it's listed under: the Delivery tab shows only the delivery facts
  mode: "PICKUP" | "DELIVERY";
  recent?: boolean;
  distance?: string | null;
  // On the Delivery tab, a store that delivers but not to the customer's address
  outsideAddress?: string | null;
  money: (cents: number) => string | null | undefined;
  onSelect: VoidFunction;
  onPickupInstead?: VoidFunction;
};

// Store card in the catering picker (C2): what the store needs from a catering order, so stores can be compared
const CateringStoreCard: FC<Props> = ({
  site,
  mode,
  recent,
  distance,
  outsideAddress,
  money,
  onSelect,
  onPickupInstead,
}) => {
  const c = cateringCapabilities(site);
  const open = isStoreOpen(site);
  const address = (
    site.address.address1?.trim() ||
    site.address.city?.trim() ||
    ""
  ).replace(/,\s*New Zealand$/i, "");
  const minText = (cents: number) =>
    cents ? ` · min. order ${money(cents)}` : "";

  const facts = (
    <span className={styles.facts}>
      {c.lead && (
        <span className={styles.fact}>
          <TimeIcon color="currentColor" />
          {c.lead.short}
        </span>
      )}
      {c.pickup && (mode === "PICKUP" || outsideAddress) && (
        <span className={styles.fact}>
          <OrderTypePickupIcon />
          <span>
            <b>Pickup</b>
            {minText(c.minPickup)}
          </span>
        </span>
      )}
      {c.delivery && mode === "DELIVERY" && !outsideAddress && (
        <span className={styles.fact}>
          <OrderTypeDeliveryIcon />
          <span>
            <b>Delivery</b>
            {minText(c.minDelivery)}
          </span>
        </span>
      )}
      {c.delivery && mode === "PICKUP" && (
        <span className={styles.fact}>
          <OrderTypeDeliveryIcon />
          <span>
            <b>Delivery</b>
            {minText(c.minDelivery)}
          </span>
        </span>
      )}
      {outsideAddress && (
        <span className={clsx(styles.fact, styles.factMuted)}>
          <OrderTypeDeliveryIcon />
          Doesn&apos;t deliver to {outsideAddress}
        </span>
      )}
    </span>
  );

  return (
    <li className={styles.cardItem}>
      <button
        type="button"
        className={styles.card}
        onClick={outsideAddress ? onPickupInstead : onSelect}
      >
        <span className={styles.header}>
          <span className={styles.titleBlock}>
            <span className={styles.nameLine}>
              <span className={styles.name}>{site.name}</span>
              {recent && <span className={styles.recent}>Recent</span>}
            </span>
            {(address || distance) && (
              <span className={styles.address}>
                {[address, distance].filter(Boolean).join(" · ")}
              </span>
            )}
          </span>
          <span
            className={clsx(styles.status, open ? styles.open : styles.closed)}
          >
            {open ? "Open" : "Closed now"}
          </span>
        </span>
        {!open && (
          <span className={styles.closedLine}>
            {getStoreTime(site)}. You can still order ahead now.
          </span>
        )}
        {facts}
        {!c.delivery && (
          <span className={styles.tags}>
            <span className={styles.tag}>Pickup only</span>
          </span>
        )}
        {outsideAddress && (
          <span className={styles.pickupInstead}>Pick up here instead</span>
        )}
      </button>
    </li>
  );
};

export default CateringStoreCard;
